// Pinia store for the app-wide single-line prompt dialog.
//
// Replaces native `window.prompt()`, which renders an OS dialog that
// breaks the design system and can't be styled or validated. Usage:
//
//     import { usePromptStore } from '@/stores/prompt';
//     const prompt = usePromptStore();
//     const name = await prompt.ask({
//       title: 'New file',
//       label: 'File name',
//       placeholder: 'index.html',
//       validate: (v) => v.includes('/') ? 'No slashes in a name.' : null,
//     });
//     if (name === null) return;   // cancelled
//
// One <PromptModal/> mounted in AppShell renders whichever request is
// active and resolves the promise with the entered string (trimmed) or
// `null` on cancel. Same single-pending policy as the confirm store.

import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface PromptRequest {
  title:         string;
  /** Field label above the input. */
  label?:        string;
  /** Optional helper line under the title. */
  body?:         string;
  placeholder?:  string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?:  string;
  /** Return an error string to block submit, or null/'' to allow. */
  validate?:     (value: string) => string | null;
}

interface PendingRequest extends PromptRequest {
  resolve: (value: string | null) => void;
}

export const usePromptStore = defineStore('prompt', () => {
  const pending = ref<PendingRequest | null>(null);

  function ask(req: PromptRequest): Promise<string | null> {
    if (pending.value) pending.value.resolve(null);
    return new Promise<string | null>((resolve) => {
      pending.value = { ...req, resolve };
    });
  }

  function resolveAndClose(value: string | null): void {
    const cur = pending.value;
    pending.value = null;
    if (cur) cur.resolve(value);
  }

  return { pending, ask, resolveAndClose };
});
